"use client";

import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { ReferralEligibilityCard } from "@/src/components/tasks/referral-eligibility-card";
import { resolveStediEligibilityDisplay } from "@/src/lib/clearinghouse/stedi/extract-eligibility-summary";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { formatIsoDate, formatUSPhoneNumber } from "@/src/lib/utils/date-and-phone-utils";
import { Referral } from "@/src/models/documents/referral";
import { getFullAddress, getPatientName } from "@/src/models/patient";

const TCO_REFERRAL_TASK_TYPE = "Refer";

function NotProvided() {
  return <span className="text-muted-foreground italic">Not provided</span>;
}

interface TCOReferralInstructionsProps {
  referral: Referral;
  documentLabel: string;
  modmedUploadFolder: string;
  assignTo: string;
  priority: string;
  taskDetails: string;
  note: string;
  taskId: string;
}

export function TCOReferralInstructions({
  referral,
  documentLabel,
  modmedUploadFolder,
  assignTo,
  priority,
  taskDetails,
  note,
  taskId,
}: TCOReferralInstructionsProps) {
  const { eligibilityCheck } = useStediEligibilityCheck(referral.id);
  const patient = referral.patient;
  const patientName = patient ? getPatientName(patient) : null;
  const patientDob = patient?.dateOfBirth ? formatIsoDate(patient.dateOfBirth) : null;
  // TCO wants the mobile number surfaced here (front desk texts/calls patients on mobile), unlike the
  // shared getPatientPhoneNumber helper which defaults to home phone first.
  const patientPhoneRaw = patient
    ? (patient.mobilePhoneNumber ?? patient.homePhoneNumber ?? patient.workPhoneNumber ?? null)
    : null;
  const patientPhone = patientPhoneRaw ? formatUSPhoneNumber(patientPhoneRaw) : null;
  const patientAddress = patient ? getFullAddress(patient) : null;
  const resolvedEligibilityStatus = eligibilityCheck
    ? resolveStediEligibilityDisplay(eligibilityCheck).eligibilityStatus
    : null;
  const eligibilityStatus =
    resolvedEligibilityStatus === "active" || resolvedEligibilityStatus === "inactive"
      ? resolvedEligibilityStatus
      : null;

  return (
    <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">
      <EhrInstructionsCard>
        <div className="flex flex-col gap-4 text-sm">
          <span className="font-medium text-center">TCO Referral Instructions:</span>
          <p className="text-muted-foreground">
            Reference for the ModMed task created for this referral. Click any value to copy.
          </p>

          <div className="space-y-3">
            <div>
              <span className="text-muted-foreground">Document Label: </span>
              {documentLabel ? <CopyableContent text={documentLabel} /> : <NotProvided />}
            </div>
            <div>
              <span className="text-muted-foreground">ModMed Upload Folder: </span>
              {modmedUploadFolder ? <CopyableContent text={modmedUploadFolder} /> : <NotProvided />}
            </div>
            <div>
              <span className="text-muted-foreground">Assign To: </span>
              {assignTo ? <CopyableContent text={assignTo} /> : <NotProvided />}
            </div>
            <div>
              <span className="text-muted-foreground">Task Type: </span>
              <CopyableContent text={TCO_REFERRAL_TASK_TYPE} />
            </div>
            <div>
              <span className="text-muted-foreground">Task Detail: </span>
              {taskDetails ? <CopyableContent text={taskDetails} /> : <NotProvided />}
            </div>
            <div>
              <span className="text-muted-foreground">ModMed Priority: </span>
              {priority ? <CopyableContent text={priority} /> : <NotProvided />}
            </div>
            <div>
              <span className="text-muted-foreground">Notes: </span>
              {note ? <CopyableContent text={note} textClassName="whitespace-pre-wrap" /> : <NotProvided />}
            </div>
          </div>
        </div>
      </EhrInstructionsCard>

      <ReferralEligibilityCard referralId={referral.id} taskId={taskId} />

      <div className="text-sm">
        <span className="font-semibold text-foreground">Patient Demographics</span>
        <div className="ml-4 mt-1 flex flex-col gap-1">
          <div>
            <span className="text-muted-foreground">Name: </span>
            {patientName ? <CopyableContent text={patientName} /> : <NotProvided />}
          </div>
          <div>
            <span className="text-muted-foreground">Date of Birth: </span>
            {patientDob ? <CopyableContent text={patientDob} /> : <NotProvided />}
          </div>
          <div>
            <span className="text-muted-foreground">Phone: </span>
            {patientPhone ? <CopyableContent text={patientPhone} /> : <NotProvided />}
          </div>
          <div>
            <span className="text-muted-foreground">Address: </span>
            {patientAddress ? <CopyableContent text={patientAddress} /> : <NotProvided />}
          </div>
        </div>
      </div>

      <PatientCard patient={referral.patient} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && (
        <CoverageCard
          coverage={referral.primaryCoverage}
          type="Primary"
          isEditable
          eligibilityStatus={eligibilityStatus}
        />
      )}
      {referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable />}
    </div>
  );
}
